import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import axios from "axios";

export const AuthContext = createContext(null);

const TOKEN_KEY = "tra_auth_token";
const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// -------------------------------------------------------------
// Axios: attach Bearer token from localStorage on every request
// -------------------------------------------------------------
axios.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  // keep cookies flowing too for legacy endpoints that still rely on the httpOnly cookie
  config.withCredentials = true;
  return config;
}, (err) => Promise.reject(err));

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const saveToken = (token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
  };

  const clearToken = () => localStorage.removeItem(TOKEN_KEY);

  // -------------------------------------------------------------
  // Session restore — hit /auth/me if we already have a token
  // -------------------------------------------------------------
  const checkAuth = async () => {
    // No token → anonymous visitor (public marketing pages). Skip the /me call entirely
    // so we don't fire a 401 that shows up as a console error on every public page view.
    if (!localStorage.getItem(TOKEN_KEY)) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      setMustChangePassword(!!response.data?.mustChangePassword);
      return response.data;
    } catch (err) {
      // Only forget the session when the SERVER actually rejects the token (401/403).
      // On a network/offline error (no response, or 5xx) keep the token so the session
      // survives reconnect — critical so a student mid-exam isn't logged out by a brief drop.
      const status = err?.response?.status;
      if (status === 401 || status === 403) { setUser(null); clearToken(); }
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------
  // Contract §3 auth methods
  // -------------------------------------------------------------
  const loginPassword = async (username, password) => {
    const res = await axios.post(`${API_URL}/api/auth/login/password`, { username, password });
    saveToken(res.data.token);
    setUser(res.data.user);
    setMustChangePassword(!!res.data.mustChangePassword);
    return res.data;
  };

  const loginPin = async (username, pin) => {
    const res = await axios.post(`${API_URL}/api/auth/login/pin`, { username, pin });
    saveToken(res.data.token);
    setUser(res.data.user);
    setMustChangePassword(!!res.data.user?.mustChangePassword);
    return res.data;
  };

  const sendOtp = async (phone) => {
    const res = await axios.post(`${API_URL}/api/auth/send-otp`, { phone });
    return res.data; // { devMode, devOtp }
  };

  const verifyOtp = async (phone, otp) => {
    const res = await axios.post(`${API_URL}/api/auth/verify-otp`, { phone, otp });
    saveToken(res.data.token);
    setUser(res.data.user);
    setMustChangePassword(!!res.data.user?.mustChangePassword);
    return res.data;
  };

  const changePassword = async (newPassword, currentPassword) => {
    const body = { newPassword };
    if (currentPassword) body.currentPassword = currentPassword;
    const res = await axios.post(`${API_URL}/api/auth/change-password`, body);
    saveToken(res.data.token);
    setUser(res.data.user);
    setMustChangePassword(false);
    return res.data;
  };

  const changePin = async (newPin) => {
    const res = await axios.post(`${API_URL}/api/auth/change-pin`, { newPin });
    return res.data;
  };

  // -------------------------------------------------------------
  // Legacy aliases (still used by existing pages) — proxy to the new contract endpoints
  // -------------------------------------------------------------
  const login = (email, password) => loginPassword(email, password);
  const loginQuick = (identifier, password) => loginPassword(identifier, password);

  const logout = async () => {
    try {
      await Promise.allSettled([
        axios.post(`${API_URL}/api/auth/logout`, {}),
        axios.post(`${API_URL}/api/mobile/auth/logout`, {}),
      ]);
    } catch (e) {
      /* ignore — token still cleared below */
    }
    clearToken();
    setUser(null);
    setMustChangePassword(false);
  };

  const value = useMemo(
    () => ({
      user,
      setUser,
      loading,
      mustChangePassword,
      // contract methods
      loginPassword,
      loginPin,
      sendOtp,
      verifyOtp,
      changePassword,
      changePin,
      // legacy aliases
      login,
      loginQuick,
      logout,
      checkAuth,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, mustChangePassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
